import { useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useApp } from '../state/app.js';
import { Spinner } from '../ui/common.js';
import { LeaveStatusIcon } from '../ui/status.js';
import { IconPlus } from '../ui/icons.js';
import { mmdd } from '../lib/format.js';
import { LeaveModal } from '../modals/LeaveModal.js';

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }): JSX.Element {
  return (
    <div className="dx-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{label}</div>
      <div className="tnum" style={{ fontSize: 28, fontWeight: 700, marginTop: 6, letterSpacing: '-.02em', color: color ?? '#1F2937' }}>
        {value}<span style={{ fontSize: 14, color: '#9CA3AF', fontWeight: 600 }}>일</span>
      </div>
    </div>
  );
}

export function Leave(): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const bal = useQuery({ queryKey: ['leave', 'balance'], queryFn: () => api.leaveBalance() });
  const mine = useQuery({ queryKey: ['leave', 'mine'], queryFn: () => api.leaveMine() });
  const b = bal.data?.balance;
  const remaining = b?.remaining ?? 0;
  const refresh = () => qc.invalidateQueries({ queryKey: ['leave'] });

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 14, marginBottom: 16 }}>
        <SummaryCard label="잔여 연차" value={remaining} color="#2563EB" />
        <SummaryCard label="사용" value={b?.used ?? 0} />
        <SummaryCard label="승인 대기" value={b?.pending ?? 0} color="#B45309" />
        <SummaryCard label="올해 부여" value={b?.granted ?? 15} />
      </div>

      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>내 휴가 신청 내역</div>
          <button className="dx-btn dx-btn-primary" style={{ marginLeft: 'auto', fontSize: 13, padding: '8px 15px' }} onClick={() => setOpen(true)}>
            <IconPlus />휴가 신청
          </button>
        </div>
        {mine.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : (mine.data?.items.length ?? 0) === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>신청 내역이 없습니다.</div>
        ) : (
          mine.data!.items.map((l) => (
            <div key={l.id} className="dx-row" style={{ padding: '13px 18px' }}>
              <div style={{ width: 64, flexShrink: 0, fontSize: 14, fontWeight: 600 }}>{l.type}</div>
              <div style={{ width: 116, flexShrink: 0, fontSize: 13, color: '#6B7280' }}>
                {mmdd(l.startDate)}{l.endDate !== l.startDate ? ` ~ ${mmdd(l.endDate)}` : ''}
              </div>
              <div className="tnum" style={{ width: 46, flexShrink: 0, fontSize: 13, color: '#6B7280' }}>{l.days}일</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.reason ?? ''}</div>
              <LeaveStatusIcon s={l.status} />
            </div>
          ))
        )}
      </div>

      {open && <LeaveModal remaining={remaining} onClose={() => setOpen(false)} onDone={(m) => { flash(m); refresh(); }} />}
    </div>
  );
}
